/**
 * Pure, display-only helpers for the Network monitor's internet and store-PC
 * status. Kept free of React and network code so they are unit-testable.
 *
 * Since 2026-10-05 "Down" means the store's internet is down, decided by the
 * api from the provider-side check (the UniFi cloud for a store with a UniFi
 * router, else Cato, else a ping from the Otto PC). The
 * store back-office PC probe no longer makes a store Down. When the internet
 * status is unknown, or the PC has stopped reporting, the page shows a small
 * grey note instead of a red badge.
 */

import { formatDateTime } from './dateUtils';
import type { NetmonSite } from './useNetmon';

/** "UniFi", "Cato" or "ping", for the tooltip suffix. */
export function internetSourceLabel(source: string | null | undefined): string {
  if (source === 'unifi') return 'UniFi';
  if (source === 'cato') return 'Cato';
  if (source === 'ping') return 'ping';
  return '';
}

/** "Internet down since Oct 5, 1:40 PM (Cato)". Null when internet is not down. */
export function internetDownText(s: NetmonSite): string | null {
  if (s.internet_status !== 'down') return null;
  const src = internetSourceLabel(s.internet_source);
  const since = s.internet_since ? ` since ${formatDateTime(s.internet_since)}` : '';
  return `Internet down${since}${src ? ` (${src})` : ''}`;
}

/** Grey note text for an unknown internet status, e.g. "Internet unknown: Router blocks ping". */
export function internetUnknownText(s: NetmonSite): string | null {
  if (s.internet_status !== 'unknown') return null;
  return s.internet_reason ? `Internet unknown: ${s.internet_reason}` : 'Internet unknown';
}

/** Grey note when the store PC has stopped reporting. Null when it is reporting. */
export function pcNotReportingText(s: NetmonSite): string | null {
  // Older api responses lack pc_reporting; treat them as reporting so the note
  // never appears from missing data.
  if (s.pc_reporting !== false) return null;
  const last = s.pc_last_sample_at ?? s.last_sample_at;
  return last
    ? `Store PC not reporting since ${formatDateTime(last)}`
    : 'Store PC has never reported';
}

/**
 * Short reason for a non-green badge, for its tooltip. Combines all triggering
 * conditions joined by ' · '. Empty string for a healthy site.
 */
export function siteHealthReason(s: NetmonSite, now: number = Date.now()): string {
  if (s.health === 'green') return '';

  const parts: string[] = [];
  const down = internetDownText(s);
  if (down) parts.push(down);

  // Compatibility with an api that predates internet_status: it still used a
  // stale PC sample as Down, so explain that the old way.
  if (s.internet_status === undefined && s.health === 'red') {
    if (s.last_sample_at) {
      const t = new Date(s.last_sample_at).getTime();
      if (Number.isFinite(t)) {
        const minutes = Math.floor((now - t) / 60_000);
        if (minutes >= 20) parts.push(`No samples in ${minutes}m`);
      }
    } else {
      parts.push('Never reported');
    }
  }

  if (s.recent_flap_down > 0) parts.push('Gateway flap-down');

  if (s.worst_loss_pct != null) {
    if (s.worst_loss_pct > 20) {
      parts.push(`Heavy loss ${s.worst_loss_pct.toFixed(1)}% (critical)`);
    } else if (s.worst_loss_pct >= 5) {
      parts.push(`Loss ${s.worst_loss_pct.toFixed(1)}%`);
    }
  }
  if (s.worst_latency_ms != null && s.worst_latency_ms >= 100) {
    parts.push(`Latency ${s.worst_latency_ms.toFixed(0)}ms`);
  }
  if (s.any_dns_failure > 0) parts.push('DNS failures');
  if (s.any_tcp_unreached > 0) parts.push('TCP unreachable');

  return parts.length > 0 ? parts.join(' · ') : 'Threshold breached';
}
